import { isNil } from 'lodash';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { ExternalBatchContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { FormScrollView } from '@shared/scenes/entity/components/FormScrollView';
import { FormScrollProvider } from '@shared/scenes/entity/context';
import { CardEntityEditForm } from '@shared/scenes/entity/edit/components/CardEntityEditForm';
import { useEditCardEntity } from '@shared/scenes/entity/edit/hooks/useEditCardEntity';
import { useChannel } from '@shared/util/hooks';
import { ExternalCardActionMenu } from './ExternalCardActionMenu';
import styles from './ReadOnlyCardEntityForm.module.css';

export function ReadOnlyCardEntityForm({
  channelId,
  templateSlug,
  entityId,
}: {
  channelId: number;
  templateSlug: string;
  entityId: string;
}) {
  const channel = useChannel({ channelId });
  const workflowTemplateId = channel?.workflowTemplateId;
  const channelSlug = channel?.slug;
  if (isNil(channelSlug)) {
    return <LoadingIndicator fill />;
  }

  return (
    <FormScrollProvider>
      <ExternalBatchContextProvider>
        <Form
          workflowTemplateId={workflowTemplateId ?? undefined}
          templateSlug={templateSlug}
          entityId={entityId}
        />
      </ExternalBatchContextProvider>
    </FormScrollProvider>
  );
}

function Form({
  workflowTemplateId,
  templateSlug,
  entityId,
}: {
  workflowTemplateId?: string;
  templateSlug: string;
  entityId: string;
}) {
  const { components, fields, updateField, isLoading, viewTemplate, viewTemplateTitle } =
    useEditCardEntity({
      entityId,
      workflowTemplateId,
      viewTemplateId: templateSlug,
      isReadOnly: true,
    });

  return (
    <View className={styles.container}>
      {isLoading && <LoadingIndicator fill />}
      {!isLoading && (
        <>
          <View className={styles.header}>
            <View row centerVertically className={styles.headerContent}>
              <Text size="h4" numberOfLines={2} className={styles.headerTitle} flex balance>
                {viewTemplateTitle}
              </Text>
              <View className={styles.optionsButton}>
                <ExternalCardActionMenu entityId={entityId} />
              </View>
            </View>
          </View>
          <FormScrollView>
            <View className={styles.form}>
              <CardEntityEditForm
                components={components}
                fields={fields}
                updateField={updateField}
                errors={[]}
                workflowTemplateId={workflowTemplateId}
                viewTemplateId={viewTemplate?.id}
                entityId={entityId}
              />
            </View>
          </FormScrollView>
        </>
      )}
    </View>
  );
}
